import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { Icon } from 'Components/UI/Icon/Icon';
import { IconName } from 'Components/UI/Icon/IconClasses';
import {
  BimMeasureMode,
  BimMeasurement,
  BimSectionPlane,
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { BimSectionPanel } from './BimSectionPanel';
import { ToolbarContainer, Pill, ToolbarButton, Divider } from './styles';

const ICON_COLOR = '#6b7280';
const ICON_COLOR_ACTIVE = '#1a73e8';

interface IToolProps {
  icon: IconName;
  iconSize?: number;
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick(): void;
}

/** "Ocultar seleção (12)" — o número só aparece quando há mais de um elemento. */
const countLabel = (label: string, count: number): string =>
  (count > 1 ? `${label} (${count})` : label);

const Tool: FC<IToolProps> = ({ icon, iconSize, label, disabled, active, onClick }) => (
  <Tooltip title={label} placement="top">
    <ToolbarButton
      type="button"
      aria-label={label}
      $active={!!active}
      disabled={disabled}
      onClick={() => !disabled && onClick()}
    >
      <Icon icon={icon} customSize={iconSize || 18} customColor={active ? ICON_COLOR_ACTIVE : ICON_COLOR} />
    </ToolbarButton>
  </Tooltip>
);

// Só `enabled`/`onFit` são obrigatórios: cada grupo de ferramentas aparece
// quando o seu handler é passado. É o que permite o federado (que ainda não tem
// árvore nem propriedades) usar a MESMA toolbar sem props no-op.
interface IBim3DToolbarProps {
  enabled: boolean;
  onFit(): void;
  hasSelection?: boolean;
  /** Quantos elementos a seleção cobre — vai no tooltip de isolar/ocultar. */
  selectionCount?: number;
  hasTree?: boolean;
  isolated?: boolean;
  multiSelect?: boolean;
  ghost?: boolean;
  onToggleMultiSelect?(): void;
  onToggleGhost?(): void;
  treeOpen?: boolean;
  propertiesOpen?: boolean;
  sectionOpen?: boolean;
  section?: BimSectionPlane;
  onFitSelection?(): void;
  onIsolate?(): void;
  onHide?(): void;
  /** Ghost na seleção: translúcido, mas ainda clicável. */

  onToggleTree?(): void;
  onToggleProperties?(): void;
  onToggleSection?(): void;
  onSectionChange?(next: Partial<BimSectionPlane>): void;
  measureMode?: BimMeasureMode;
  measurements?: BimMeasurement[];
  onToggleMeasure?(mode: Exclude<BimMeasureMode, 'none'>): void;
  onClearMeasurements?(): void;
}

/**
 * Barra de ferramentas do viewer 3D, no formato do visualizador web da
 * Autodesk: pílulas flutuantes agrupadas por função (navegação · visibilidade ·
 * corte · painéis), ancoradas na base da cena.
 *
 * Orbit/pan/zoom não são botões: já são gestos de mouse do motor (arrastar,
 * botão direito, roda). Botão de modo só existiria pra duplicar o que o gesto
 * faz melhor.
 */
export const Bim3DToolbar: FC<IBim3DToolbarProps> = ({
  enabled,
  hasSelection,
  selectionCount = 0,
  hasTree,
  isolated,
  multiSelect,
  ghost,
  onToggleMultiSelect,
  onToggleGhost,
  treeOpen,
  propertiesOpen,
  sectionOpen,
  section,
  onFit,
  onFitSelection,
  onIsolate,
  onHide,
  onToggleTree,
  onToggleProperties,
  onToggleSection,
  onSectionChange,
  measureMode = 'none',
  measurements = [],
  onToggleMeasure,
  onClearMeasurements,
}) => {
  const { t } = useTranslation('viewer');
  // O grupo de medição abre a partir do próprio botão, como um submenu: o valor
  // já aparece na cena, então não há painel — só as ferramentas.
  const [measureOpen, setMeasureOpen] = useState(false);
  // Enquadrar e visibilidade seguem o mesmo padrão do grupo de medição: um
  // botão abre as opções, em vez de seis ícones lado a lado na barra.
  const [frameOpen, setFrameOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);

  const hasVisibilityTools = !!onIsolate && !!onHide;
  const hasPanelTools = !!onToggleTree || !!onToggleProperties;

  // Fechar o grupo sai da ferramenta: deixar o modo ligado sem os botões à
  // vista faria o clique no 3D virar ponto de medida sem nada indicando isso.
  const toggleMeasureGroup = () => {
    if (measureOpen && measureMode !== 'none') { onToggleMeasure!(measureMode); }
    setMeasureOpen((current) => !current);
  };

  return (
    <ToolbarContainer>
      {onToggleMeasure && measureOpen && (
        <Pill>
          <Tool
            icon="measureDistance"
            label={t('bim3dToolbar.measureDistance')}
            disabled={!enabled}
            active={measureMode === 'distance'}
            onClick={() => onToggleMeasure('distance')}
          />
          <Tool
            icon="measureArea"
            label={t('bim3dToolbar.measureArea')}
            disabled={!enabled}
            active={measureMode === 'area'}
            onClick={() => onToggleMeasure('area')}
          />
          <Divider />
          <Tool
            icon="excluir"
            label={t('bim3dToolbar.measureClear')}
            disabled={!enabled || measurements.length === 0}
            onClick={() => onClearMeasurements?.()}
          />
        </Pill>
      )}

      {frameOpen && (
        <Pill>
          <Tool
            icon="zoomToExtent"
            label={t('bim3dToolbar.fit')}
            disabled={!enabled}
            onClick={onFit}
          />
          {onFitSelection && (
            <Tool
              icon="zoomToBox"
              label={t('bim3dToolbar.fitSelection')}
              disabled={!enabled || !hasSelection}
              onClick={onFitSelection}
            />
          )}
        </Pill>
      )}

      {hasVisibilityTools && visibilityOpen && (
        <Pill>
          <Tool
            icon="borderCorners"
            // A contagem no tooltip evita a surpresa de "sumiu mais do que eu
            // selecionei" quando a seleção veio de um nó da árvore.
            label={countLabel(t('bim3dToolbar.isolate'), selectionCount)}
            disabled={!enabled || !hasSelection}
            active={isolated}
            onClick={onIsolate!}
          />
          <Tool
            icon="notVisualizar"
            iconSize={14}
            label={countLabel(t('bim3dToolbar.hide'), selectionCount)}
            disabled={!enabled || !hasSelection}
            onClick={onHide!}
          />
          {onToggleGhost && (
            // Um botão só: com seleção, ela fica translúcida; sem seleção, o
            // modelo inteiro.
            <Tool
              icon="eyeCheck"
              iconSize={20}
              label={countLabel(
                hasSelection ? t('bim3dToolbar.ghostSelection') : t('bim3dToolbar.ghostModel'),
                hasSelection ? selectionCount : 0,
              )}
              disabled={!enabled}
              active={ghost}
              onClick={onToggleGhost}
            />
          )}
        </Pill>
      )}

      {sectionOpen && section && onSectionChange && onToggleSection && (
        <BimSectionPanel
          section={section}
          onChange={onSectionChange}
          onClose={onToggleSection}
        />
      )}

      <Pill>
        <Tool
          icon="zoomToExtent"
          label={t('bim3dToolbar.frameGroup')}
          disabled={!enabled}
          active={frameOpen}
          onClick={() => setFrameOpen((current) => !current)}
        />

        {onToggleMultiSelect && (
          <>
            <Divider />
            <Tool
              icon="doubleCheck"
              label={t('bim3dToolbar.multiSelect')}
              disabled={!enabled}
              active={multiSelect}
              onClick={onToggleMultiSelect}
            />
          </>
        )}

        {hasVisibilityTools && (
          <>
            <Divider />
            <Tool
              icon="visualizar"
              iconSize={14}
              label={t('bim3dToolbar.visibilityGroup')}
              disabled={!enabled}
              active={visibilityOpen || isolated || !!ghost}
              onClick={() => setVisibilityOpen((current) => !current)}
            />
          </>
        )}

        {onToggleMeasure && (
          <>
            <Divider />
            <Tool
              icon="measure"
              label={t('bim3dToolbar.measure')}
              disabled={!enabled}
              active={measureOpen || measureMode !== 'none'}
              onClick={toggleMeasureGroup}
            />
          </>
        )}

        {onToggleSection && (
          <>
            <Divider />
            <Tool
              icon="section"
              iconSize={20}
              label={t('bim3dToolbar.section')}
              disabled={!enabled}
              active={sectionOpen || section?.enabled}
              onClick={onToggleSection}
            />
          </>
        )}

        {hasPanelTools && <Divider />}
        {onToggleTree && (
          <Tool
            icon="listList"
            label={t('bim3dToolbar.tree')}
            disabled={!enabled || !hasTree}
            active={treeOpen}
            onClick={onToggleTree}
          />
        )}
        {onToggleProperties && (
          <Tool
            icon="informacaoAdicional"
            label={t('bim3dToolbar.properties')}
            disabled={!enabled}
            active={propertiesOpen}
            onClick={onToggleProperties}
          />
        )}
      </Pill>
    </ToolbarContainer>
  );
};
